import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Card, 
  Button, 
  Typography, 
  Modal, 
  Drawer,
  Form, 
  Space, 
  message, 
  Tabs, 
  Row,
  Col,
  Statistic,
  Divider,
  Progress,
  Tag,
  List,
  Avatar,
  Badge,
  Tooltip,
  Alert,
  Collapse,
  Timeline,
  Select,
  Input,
  DatePicker,
  Popconfirm,
  Table,
  Steps,
  Radio,
  Checkbox,
  Calendar,
  InputNumber,
  Upload
} from 'antd';
import { 
  HomeOutlined, 
  PlusOutlined,
  ArrowLeftOutlined,
  TrophyOutlined,
  StarOutlined,
  FireOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  BarChartOutlined,
  BookOutlined,
  AimOutlined,
  BulbOutlined,
  InfoCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SettingOutlined,
  FileTextOutlined,
  HistoryOutlined,
  DollarOutlined,
  UserOutlined,
  TeamOutlined,
  HeartOutlined,
  MessageOutlined,
  MedicineBoxOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { familyConfig } from '../config/family';
import axios from 'axios';
import dayjs from 'dayjs';

// 配置axios基础URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';
if (typeof window !== 'undefined') {
  const storedToken = localStorage.getItem('token');
  if (storedToken) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
  }
}

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { Option } = Select;
const { TextArea } = Input;
const { Step } = Steps;
const { Group: RadioGroup } = Radio;

const INITIAL_FAMILY_STATS = {
  totalMembers: 0,
  totalBudget: 0,
  completedChores: 0,
  upcomingEvents: 0,
  recentMeetings: 0,
  childrenCount: 0,
  healthRecords: 0,
  communicationRecords: 0
};

// 家庭财务组件
const FamilyFinance = () => {
  const [financeData, setFinanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFinanceModal, setShowFinanceModal] = useState(false);
  const [selectedFinance, setSelectedFinance] = useState(null);
  const [financeForm] = Form.useForm();

  // 获取财务数据
  const fetchFinanceData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(familyConfig.apiEndpoints.finance);
      if (response.data.success) {
        setFinanceData(response.data.finance || []);
      } else {
        message.error(response.data.message || '获取财务数据失败');
      }
    } catch (error) {
      console.error('获取财务数据失败:', error);
      message.error('获取财务数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 打开财务记录弹窗
  const handleOpenFinance = (finance = null) => {
    setSelectedFinance(finance);
    if (finance) {
      financeForm.setFieldsValue(finance);
    } else {
      financeForm.resetFields();
    }
    setShowFinanceModal(true);
  };

  // 保存财务记录
  const handleSaveFinance = async (values) => {
    try {
      const requestBody = {
        ...values
      };

      const url = selectedFinance 
        ? `${familyConfig.apiEndpoints.updateFinance}/${selectedFinance.id}`
        : familyConfig.apiEndpoints.createFinance;
      
      const method = selectedFinance ? 'PUT' : 'POST';
      const token = localStorage.getItem('token') || '';

      let response;
      if (selectedFinance) {
        response = await axios.put(`${familyConfig.apiEndpoints.updateFinance}/${selectedFinance.id}`, requestBody);
      } else {
        response = await axios.post(familyConfig.apiEndpoints.createFinance, requestBody);
      }

      if (response.data.success) {
        message.success(selectedFinance ? '更新成功' : '创建成功');
        setShowFinanceModal(false);
        fetchFinanceData();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存财务记录失败:', error);
      message.error('操作失败');
    }
  };

  // 删除财务记录
  const handleDeleteFinance = async (id) => {
    try {
      const response = await axios.delete(`${familyConfig.apiEndpoints.deleteFinance}/${id}`);
      if (response.data.success) {
        message.success('删除成功');
        fetchFinanceData();
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除财务记录失败:', error);
      message.error('删除失败');
    }
  };

  // 获取类型颜色
  const getTypeColor = (typeKey) => {
    const type = familyConfig.financeTypes.find(t => t.key === typeKey);
    return type ? type.color : '#D9D9D9';
  };

  // 获取类型名称
  const getTypeName = (typeKey) => {
    const type = familyConfig.financeTypes.find(t => t.key === typeKey);
    return type ? type.name : '未知';
  };

  // 获取类型图标
  const getTypeIcon = (typeKey) => {
    const type = familyConfig.financeTypes.find(t => t.key === typeKey);
    return type ? type.icon : '📝';
  };

  // 计算总金额
  const totalAmount = financeData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  // 组件加载时获取数据
  useEffect(() => {
    fetchFinanceData();
  }, []);

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{ 
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
              border: 'none',
              transition: 'all 0.3s ease'
            }}
            bodyStyle={{ padding: '6px' }}
          >
            <Statistic
              title="总记录数"
              value={financeData.length}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: familyConfig.bgColor }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{ 
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
              border: 'none',
              transition: 'all 0.3s ease'
            }}
            bodyStyle={{ padding: '6px' }}
          >
            <Statistic
              title="总金额"
              value={totalAmount}
              prefix={<DollarOutlined />}
              suffix="元"
              valueStyle={{ color: '#52C41A' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{ 
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
              border: 'none',
              transition: 'all 0.3s ease'
            }}
            bodyStyle={{ padding: '6px' }}
          >
            <Statistic
              title="活跃记录"
              value={financeData.filter(item => item.status === 'active').length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#1890FF' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{ 
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
              border: 'none',
              transition: 'all 0.3s ease'
            }}
            bodyStyle={{ padding: '6px' }}
          >
            <Statistic
              title="本月新增"
              value={financeData.filter(item => {
                const createdDate = new Date(item.created_at);
                const now = new Date();
                return createdDate.getMonth() === now.getMonth() && 
                       createdDate.getFullYear() === now.getFullYear();
              }).length}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#FAAD14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 操作按钮 */}
      <div style={{ marginBottom: '16px' }}>
        <Button 
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenFinance()}
          style={{ backgroundColor: familyConfig.bgColor, borderColor: familyConfig.bgColor }}
        >
          新增财务记录
        </Button>
      </div>

      {/* 财务记录列表 */}
      <Row gutter={[16, 16]}>
        {financeData.map((finance) => (
          <Col xs={24} sm={12} md={8} lg={6} key={finance.id}>
            <Card
              hoverable
              actions={[
                <EditOutlined key="edit" onClick={() => handleOpenFinance(finance)} />,
                <Popconfirm
                  title="确定删除这条财务记录吗？"
                  onConfirm={() => handleDeleteFinance(finance.id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <DeleteOutlined key="delete" />
                </Popconfirm>
              ]}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                  {getTypeIcon(finance.type)}
                </div>
                <Title level={5} style={{ margin: '8px 0' }}>
                  {finance.title}
                </Title>
                <Tag color={getTypeColor(finance.type)} style={{ marginBottom: '8px' }}>
                  {getTypeName(finance.type)}
                </Tag>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#52C41A', marginBottom: '8px' }}>
                  ¥{parseFloat(finance.amount || 0).toLocaleString()}
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {finance.description}
                </Text>
                <div style={{ marginTop: '8px' }}>
                  <Tag color={finance.status === 'active' ? 'green' : 'orange'}>
                    {finance.status === 'active' ? '活跃' : '暂停'}
                  </Tag>
                </div>
              </div>
            </Card>
          </Col>
        ))}
        {financeData.length === 0 && (
          <Col span={24}>
            <Card
              style={{ 
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                border: 'none',
                transition: 'all 0.3s ease'
              }}
              bodyStyle={{ padding: '6px' }}
            >
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <DollarOutlined style={{ fontSize: '48px', color: '#D9D9D9', marginBottom: '16px' }} />
                <Title level={4} style={{ color: '#D9D9D9' }}>暂无财务记录</Title>
                <Text type="secondary">点击"新增财务记录"开始管理家庭财务</Text>
              </div>
            </Card>
          </Col>
        )}
      </Row>

      {/* 财务记录弹窗 */}
      <Modal
        title={`${selectedFinance ? '编辑' : '新增'}财务记录`}
        open={showFinanceModal}
        onCancel={() => setShowFinanceModal(false)}
        footer={null}
        width={600}
      >
        <Form
          form={financeForm}
          layout="vertical"
          onFinish={handleSaveFinance}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="财务类型"
                rules={[{ required: true, message: '请选择财务类型' }]}
              >
                <Select placeholder="请选择类型">
                  {familyConfig.financeTypes.map(type => (
                    <Option key={type.key} value={type.key}>
                      <span style={{ marginRight: '8px' }}>{type.icon}</span>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="状态"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select placeholder="请选择状态">
                  <Option value="active">活跃</Option>
                  <Option value="inactive">暂停</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="title"
            label="标题"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input placeholder="请输入财务记录标题" />
          </Form.Item>
          
          <Form.Item
            name="amount"
            label="金额"
            rules={[{ required: true, message: '请输入金额' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="请输入金额"
              formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\¥\s?|(,*)/g, '')}
              min={0}
            />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea rows={3} placeholder="请输入详细描述" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedFinance ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setShowFinanceModal(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// 家务分工组件
const FamilyChores = () => {
  const [choresData, setChoresData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showChoreModal, setShowChoreModal] = useState(false);
  const [selectedChore, setSelectedChore] = useState(null);
  const [choreForm] = Form.useForm();

  // 获取家务数据
  const fetchChoresData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(familyConfig.apiEndpoints.chores);
      if (response.data?.success) {
        setChoresData(response.data.chores || []);
      } else {
        message.error(response.data?.message || '获取家务数据失败');
      }
    } catch (error) {
      console.error('获取家务数据失败:', error);
      message.error('获取家务数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 打开家务记录弹窗
  const handleOpenChore = (chore = null) => {
    setSelectedChore(chore);
    if (chore) {
      choreForm.setFieldsValue(chore);
    } else {
      choreForm.resetFields();
    }
    setShowChoreModal(true);
  };

  // 保存家务记录
  const handleSaveChore = async (values) => {
    try {
      const requestBody = {
        ...values
      };

      const url = selectedChore 
        ? `${familyConfig.apiEndpoints.updateChore}/${selectedChore.id}`
        : familyConfig.apiEndpoints.createChore;
      
      const method = selectedChore ? 'PUT' : 'POST';
      let response;
      if (method === 'PUT') {
        response = await axios.put(url, requestBody);
      } else {
        response = await axios.post(url, requestBody);
      }

      if (response.data?.success) {
        message.success(selectedChore ? '更新成功' : '创建成功');
        setShowChoreModal(false);
        fetchChoresData();
      } else {
        message.error(response.data?.message || '操作失败');
      }
    } catch (error) {
      console.error('保存家务记录失败:', error);
      message.error('操作失败');
    }
  };

  // 删除家务记录
  const handleDeleteChore = async (id) => {
    try {
      const response = await axios.delete(`${familyConfig.apiEndpoints.deleteChore}/${id}`);
      if (response.data?.success) {
        message.success('删除成功');
        fetchChoresData();
      } else {
        message.error(response.data?.message || '删除失败');
      }
    } catch (error) {
      console.error('删除家务记录失败:', error);
      message.error('删除失败');
    }
  };

  // 获取类型颜色
  const getTypeColor = (typeKey) => {
    const type = familyConfig.choreTypes.find(t => t.key === typeKey);
    return type ? type.color : '#D9D9D9';
  };

  // 获取类型名称
  const getTypeName = (typeKey) => {
    const type = familyConfig.choreTypes.find(t => t.key === typeKey);
    return type ? type.name : '未知';
  };

  // 获取类型图标
  const getTypeIcon = (typeKey) => {
    const type = familyConfig.choreTypes.find(t => t.key === typeKey);
    return type ? type.icon : '📝';
  };

  // 获取状态颜色
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'in_progress':
        return 'blue';
      case 'pending':
        return 'orange';
      default:
        return 'default';
    }
  };

  // 获取状态名称
  const getStatusName = (status) => {
    switch (status) {
      case 'completed':
        return '已完成';
      case 'in_progress':
        return '进行中';
      case 'pending':
        return '待开始';
      default:
        return '未知';
    }
  };

  // 计算统计信息
  const totalChores = choresData.length;
  const completedChores = choresData.filter(item => item.status === 'completed').length;
  const pendingChores = choresData.filter(item => item.status === 'pending').length;
  const completionRate = totalChores > 0 ? Math.round((completedChores / totalChores) * 100) : 0;

  // 组件加载时获取数据
  useEffect(() => {
    fetchChoresData();
  }, []);

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="总任务数"
              value={totalChores}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: familyConfig.bgColor }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="已完成"
              value={completedChores}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52C41A' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="待开始"
              value={pendingChores}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#FAAD14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="完成率"
              value={completionRate}
              prefix={<BarChartOutlined />}
              suffix="%"
              valueStyle={{ color: '#1890FF' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 操作按钮 */}
      <div style={{ marginBottom: '16px' }}>
        <Button 
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenChore()}
          style={{ backgroundColor: familyConfig.bgColor, borderColor: familyConfig.bgColor }}
        >
          新增家务任务
        </Button>
      </div>

      {/* 家务任务列表 */}
      <Row gutter={[16, 16]}>
        {choresData.map((chore) => (
          <Col xs={24} sm={12} md={8} lg={6} key={chore.id}>
            <Card
              hoverable
              actions={[
                <EditOutlined key="edit" onClick={() => handleOpenChore(chore)} />,
                <Popconfirm
                  title="确定删除这个家务任务吗？"
                  onConfirm={() => handleDeleteChore(chore.id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <DeleteOutlined key="delete" />
                </Popconfirm>
              ]}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                  {getTypeIcon(chore.chore_type)}
                </div>
                <Title level={5} style={{ margin: '8px 0' }}>
                  {chore.chore_content}
                </Title>
                <Tag color={getTypeColor(chore.chore_type)} style={{ marginBottom: '8px' }}>
                  {getTypeName(chore.chore_type)}
                </Tag>
                <div style={{ marginBottom: '8px' }}>
                  <Text strong>{chore.member_name}</Text>
                  {chore.member_role && (
                    <Text type="secondary" style={{ marginLeft: '8px' }}>
                      ({chore.member_role})
                    </Text>
                  )}
                </div>
                <div style={{ marginTop: '8px' }}>
                  <Tag color={getStatusColor(chore.status)}>
                    {getStatusName(chore.status)}
                  </Tag>
                </div>
                {chore.notes && (
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                    {chore.notes}
                  </Text>
                )}
              </div>
            </Card>
          </Col>
        ))}
        {choresData.length === 0 && (
          <Col span={24}>
            <Card>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <TeamOutlined style={{ fontSize: '48px', color: '#D9D9D9', marginBottom: '16px' }} />
                <Title level={4} style={{ color: '#D9D9D9' }}>暂无家务任务</Title>
                <Text type="secondary">点击"新增家务任务"开始分配家庭任务</Text>
              </div>
            </Card>
          </Col>
        )}
      </Row>

      {/* 家务任务弹窗 */}
      <Modal
        title={`${selectedChore ? '编辑' : '新增'}家务任务`}
        open={showChoreModal}
        onCancel={() => setShowChoreModal(false)}
        footer={null}
        width={600}
        centered
        forceRender
        destroyOnHidden
        maskClosable={false}
        getContainer={false}
        zIndex={1000}
      >
        <Form
          form={choreForm}
          layout="vertical"
          onFinish={handleSaveChore}
          initialValues={{ status: 'pending', chore_type: 'other' }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="member_name"
                label="成员姓名"
                rules={[{ required: true, message: '请输入成员姓名' }]}
              >
                <Input placeholder="请输入成员姓名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="member_role"
                label="成员角色"
              >
                <Input placeholder="如：爸爸、妈妈、孩子" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="chore_type"
                label="任务类型"
                rules={[{ required: true, message: '请选择任务类型' }]}
              >
                <Select placeholder="请选择类型">
                  {familyConfig.choreTypes.map(type => (
                    <Option key={type.key} value={type.key}>
                      <span style={{ marginRight: '8px' }}>{type.icon}</span>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="任务状态"
                rules={[{ required: true, message: '请选择任务状态' }]}
              >
                <Select placeholder="请选择状态">
                  <Option value="pending">待开始</Option>
                  <Option value="in_progress">进行中</Option>
                  <Option value="completed">已完成</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="chore_content"
            label="任务内容"
            rules={[{ required: true, message: '请输入任务内容' }]}
          >
            <Input placeholder="请输入具体的家务任务内容" />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea rows={3} placeholder="请输入备注信息" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedChore ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setShowChoreModal(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// 家庭时间表组件
const FamilySchedule = () => {
  const [scheduleData, setScheduleData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [scheduleForm] = Form.useForm();

  // 获取时间表数据
  const fetchScheduleData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(familyConfig.apiEndpoints.schedule);
      if (response.data?.success) {
        setScheduleData(response.data.schedule || []);
      } else {
        message.error(response.data?.message || '获取时间表数据失败');
      }
    } catch (error) {
      console.error('获取时间表数据失败:', error);
      message.error('获取时间表数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 打开时间表记录弹窗
  const handleOpenSchedule = (schedule = null) => {
    setSelectedSchedule(schedule);
    if (schedule) {
      scheduleForm.setFieldsValue({
        ...schedule,
        start_date: schedule.start_date ? dayjs(schedule.start_date) : null,
        end_date: schedule.end_date ? dayjs(schedule.end_date) : null
      });
    } else {
      scheduleForm.resetFields();
    }
    setShowScheduleModal(true);
  };

  // 保存时间表记录
  const handleSaveSchedule = async (values) => {
    try {
      const requestBody = {
        ...values,
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
        end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : null
      };

      const url = selectedSchedule 
        ? `${familyConfig.apiEndpoints.updateSchedule}/${selectedSchedule.id}`
        : familyConfig.apiEndpoints.createSchedule;

      const response = selectedSchedule
        ? await axios.put(url, requestBody)
        : await axios.post(url, requestBody);

      if (response.data?.success) {
        message.success(selectedSchedule ? '更新成功' : '创建成功');
        setShowScheduleModal(false);
        fetchScheduleData();
      } else {
        message.error(response.data?.message || '操作失败');
      }
    } catch (error) {
      console.error('保存时间表记录失败:', error);
      message.error('操作失败');
    }
  };

  // 删除时间表记录
  const handleDeleteSchedule = async (id) => {
    try {
      const response = await axios.delete(`${familyConfig.apiEndpoints.deleteSchedule}/${id}`);
      if (response.data?.success) {
        message.success('删除成功');
        fetchScheduleData();
      } else {
        message.error(response.data?.message || '删除失败');
      }
    } catch (error) {
      console.error('删除时间表记录失败:', error);
      message.error('删除失败');
    }
  };

  // 获取类型颜色
  const getTypeColor = (typeKey) => {
    const type = familyConfig.activityTypes.find(t => t.key === typeKey);
    return type ? type.color : '#D9D9D9';
  };

  // 获取类型名称
  const getTypeName = (typeKey) => {
    const type = familyConfig.activityTypes.find(t => t.key === typeKey);
    return type ? type.name : '未知';
  };

  // 获取类型图标
  const getTypeIcon = (typeKey) => {
    const type = familyConfig.activityTypes.find(t => t.key === typeKey);
    return type ? type.icon : '📝';
  };

  // 获取状态颜色
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'in_progress':
        return 'blue';
      case 'planned':
        return 'orange';
      case 'cancelled':
        return 'red';
      default:
        return 'default';
    }
  };

  // 获取状态名称
  const getStatusName = (status) => {
    switch (status) {
      case 'completed':
        return '已完成';
      case 'in_progress':
        return '进行中';
      case 'planned':
        return '已计划';
      case 'cancelled':
        return '已取消';
      default:
        return '未知';
    }
  };

  // 计算统计信息
  const totalEvents = scheduleData.length;
  const completedEvents = scheduleData.filter(item => item.status === 'completed').length;
  const upcomingEvents = scheduleData.filter(item => item.status === 'planned').length;
  const completionRate = totalEvents > 0 ? Math.round((completedEvents / totalEvents) * 100) : 0;

  // 组件加载时获取数据
  useEffect(() => {
    fetchScheduleData();
  }, []);

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="总活动数"
              value={totalEvents}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: familyConfig.bgColor }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="已完成"
              value={completedEvents}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52C41A' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="即将到来"
              value={upcomingEvents}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#FAAD14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="完成率"
              value={completionRate}
              prefix={<BarChartOutlined />}
              suffix="%"
              valueStyle={{ color: '#1890FF' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 操作按钮 */}
      <div style={{ marginBottom: '16px' }}>
        <Button 
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenSchedule()}
          style={{ backgroundColor: familyConfig.bgColor, borderColor: familyConfig.bgColor }}
        >
          新增家庭活动
        </Button>
      </div>

      {/* 时间表列表 */}
      <Row gutter={[16, 16]}>
        {scheduleData.map((schedule) => (
          <Col xs={24} sm={12} md={8} lg={6} key={schedule.id}>
            <Card
              hoverable
              actions={[
                <EditOutlined key="edit" onClick={() => handleOpenSchedule(schedule)} />,
                <Popconfirm
                  title="确定删除这个家庭活动吗？"
                  onConfirm={() => handleDeleteSchedule(schedule.id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <DeleteOutlined key="delete" />
                </Popconfirm>
              ]}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                  {getTypeIcon(schedule.activity_type)}
                </div>
                <Title level={5} style={{ margin: '8px 0' }}>
                  {schedule.title}
                </Title>
                <Tag color={getTypeColor(schedule.activity_type)} style={{ marginBottom: '8px' }}>
                  {getTypeName(schedule.activity_type)}
                </Tag>
                <div style={{ marginBottom: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {schedule.start_date && (
                      <span>开始: {dayjs(schedule.start_date).format('MM-DD')}</span>
                    )}
                    {schedule.end_date && schedule.start_date !== schedule.end_date && (
                      <span style={{ marginLeft: '8px' }}>
                        结束: {dayjs(schedule.end_date).format('MM-DD')}
                      </span>
                    )}
                  </Text>
                </div>
                {schedule.location && (
                  <div style={{ marginBottom: '8px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      📍 {schedule.location}
                    </Text>
                  </div>
                )}
                <div style={{ marginTop: '8px' }}>
                  <Tag color={getStatusColor(schedule.status)}>
                    {getStatusName(schedule.status)}
                  </Tag>
                </div>
                {schedule.description && (
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                    {schedule.description}
                  </Text>
                )}
              </div>
            </Card>
          </Col>
        ))}
        {scheduleData.length === 0 && (
          <Col span={24}>
            <Card>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <CalendarOutlined style={{ fontSize: '48px', color: '#D9D9D9', marginBottom: '16px' }} />
                <Title level={4} style={{ color: '#D9D9D9' }}>暂无家庭活动</Title>
                <Text type="secondary">点击"新增家庭活动"开始安排家庭时间</Text>
              </div>
            </Card>
          </Col>
        )}
      </Row>

      {/* 时间表记录弹窗 */}
      <Modal
        title={`${selectedSchedule ? '编辑' : '新增'}家庭活动`}
        open={showScheduleModal}
        onCancel={() => setShowScheduleModal(false)}
        footer={null}
        width={600}
      >
        <Form
          form={scheduleForm}
          layout="vertical"
          onFinish={handleSaveSchedule}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="activity_type"
                label="活动类型"
                rules={[{ required: true, message: '请选择活动类型' }]}
              >
                <Select placeholder="请选择类型">
                  {familyConfig.activityTypes.map(type => (
                    <Option key={type.key} value={type.key}>
                      <span style={{ marginRight: '8px' }}>{type.icon}</span>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="活动状态"
                rules={[{ required: true, message: '请选择活动状态' }]}
              >
                <Select placeholder="请选择状态">
                  <Option value="planned">已计划</Option>
                  <Option value="in_progress">进行中</Option>
                  <Option value="completed">已完成</Option>
                  <Option value="cancelled">已取消</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="title"
            label="活动标题"
            rules={[{ required: true, message: '请输入活动标题' }]}
          >
            <Input placeholder="请输入活动标题" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="start_date"
                label="开始日期"
              >
                <DatePicker 
                  style={{ width: '100%' }}
                  placeholder="选择开始日期"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="end_date"
                label="结束日期"
              >
                <DatePicker 
                  style={{ width: '100%' }}
                  placeholder="选择结束日期"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="location"
            label="活动地点"
          >
            <Input placeholder="请输入活动地点" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="活动描述"
          >
            <TextArea rows={3} placeholder="请输入活动详细描述" />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea rows={2} placeholder="请输入备注信息" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedSchedule ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setShowScheduleModal(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// 家庭会议记录组件
const FamilyMeetings = () => {
  const [meetingsData, setMeetingsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [meetingForm] = Form.useForm();

  // 获取会议数据
  const fetchMeetingsData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(familyConfig.apiEndpoints.meetings);
      if (response.data?.success) {
        setMeetingsData(response.data.meetings || []);
      } else {
        message.error(response.data?.message || '获取会议数据失败');
      }
    } catch (error) {
      console.error('获取会议数据失败:', error);
      message.error('获取会议数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 打开会议记录弹窗
  const handleOpenMeeting = (meeting = null) => {
    setSelectedMeeting(meeting);
    if (meeting) {
      meetingForm.setFieldsValue({
        ...meeting,
        meeting_date: meeting.meeting_date ? dayjs(meeting.meeting_date) : null
      });
    } else {
      meetingForm.resetFields();
    }
    setShowMeetingModal(true);
  };

  // 保存会议记录
  const handleSaveMeeting = async (values) => {
    try {
      const requestBody = {
        meeting_type: values.meeting_type,
        title: values.title,
        meeting_date: values.meeting_date ? values.meeting_date.format('YYYY-MM-DD') : null,
        participants: values.participants || '',
        agenda: values.agenda || '',
        conclusions: values.conclusions || '',
        decisions: values.decisions || '',
        notes: values.notes || ''
      };

      const url = selectedMeeting 
        ? `${familyConfig.apiEndpoints.updateMeeting}/${selectedMeeting.id}`
        : familyConfig.apiEndpoints.createMeeting;

      const response = selectedMeeting
        ? await axios.put(url, requestBody)
        : await axios.post(url, requestBody);

      if (response.data?.success) {
        message.success(selectedMeeting ? '更新成功' : '创建成功');
        setShowMeetingModal(false);
        fetchMeetingsData();
      } else {
        message.error(response.data?.message || '操作失败');
      }
    } catch (error) {
      console.error('保存会议记录失败:', error);
      message.error('操作失败');
    }
  };

  // 删除会议记录
  const handleDeleteMeeting = async (id) => {
    try {
      const response = await axios.delete(`${familyConfig.apiEndpoints.deleteMeeting}/${id}`);
      if (response.data?.success) {
        message.success('删除成功');
        fetchMeetingsData();
      } else {
        message.error(response.data?.message || '删除失败');
      }
    } catch (error) {
      console.error('删除会议记录失败:', error);
      message.error('删除失败');
    }
  };

  // 获取类型颜色
  const getTypeColor = (typeKey) => {
    const type = familyConfig.meetingTypes.find(t => t.key === typeKey);
    return type ? type.color : '#D9D9D9';
  };

  // 获取类型名称
  const getTypeName = (typeKey) => {
    const type = familyConfig.meetingTypes.find(t => t.key === typeKey);
    return type ? type.name : '未知';
  };

  // 获取类型图标
  const getTypeIcon = (typeKey) => {
    const type = familyConfig.meetingTypes.find(t => t.key === typeKey);
    return type ? type.icon : '📝';
  };

  // 获取状态颜色
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'in_progress':
        return 'blue';
      case 'planned':
        return 'orange';
      case 'cancelled':
        return 'red';
      default:
        return 'default';
    }
  };

  // 获取状态名称
  const getStatusName = (status) => {
    switch (status) {
      case 'completed':
        return '已完成';
      case 'in_progress':
        return '进行中';
      case 'planned':
        return '已计划';
      case 'cancelled':
        return '已取消';
      default:
        return '未知';
    }
  };

  // 计算统计信息
  const totalMeetings = meetingsData.length;
  const completedMeetings = meetingsData.filter(item => item.status === 'completed').length;
  const upcomingMeetings = meetingsData.filter(item => item.status === 'planned').length;
  const completionRate = totalMeetings > 0 ? Math.round((completedMeetings / totalMeetings) * 100) : 0;

  // 组件加载时获取数据
  useEffect(() => {
    fetchMeetingsData();
  }, []);

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="总会议数"
              value={totalMeetings}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: familyConfig.bgColor }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="已完成"
              value={completedMeetings}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52C41A' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="即将召开"
              value={upcomingMeetings}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#FAAD14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="完成率"
              value={completionRate}
              prefix={<BarChartOutlined />}
              suffix="%"
              valueStyle={{ color: '#1890FF' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 操作按钮 */}
      <div style={{ marginBottom: '16px' }}>
        <Button 
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenMeeting()}
          style={{ backgroundColor: familyConfig.bgColor, borderColor: familyConfig.bgColor }}
        >
          新增家庭会议
        </Button>
      </div>

      {/* 会议记录列表 */}
      <Row gutter={[16, 16]}>
        {meetingsData.map((meeting) => (
          <Col xs={24} sm={12} md={8} lg={6} key={meeting.id}>
            <Card
              hoverable
              actions={[
                <EditOutlined key="edit" onClick={() => handleOpenMeeting(meeting)} />,
                <Popconfirm
                  title="确定删除这个家庭会议吗？"
                  onConfirm={() => handleDeleteMeeting(meeting.id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <DeleteOutlined key="delete" />
                </Popconfirm>
              ]}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                  {getTypeIcon(meeting.meeting_type)}
                </div>
                <Title level={5} style={{ margin: '8px 0' }}>
                  {meeting.title}
                </Title>
                <Tag color={getTypeColor(meeting.meeting_type)} style={{ marginBottom: '8px' }}>
                  {getTypeName(meeting.meeting_type)}
                </Tag>
                <div style={{ marginBottom: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    📅 {meeting.meeting_date ? dayjs(meeting.meeting_date).format('MM-DD') : '未设置'}
                  </Text>
                </div>
                <div style={{ marginTop: '8px' }}>
                  <Tag color={getStatusColor(meeting.status)}>
                    {getStatusName(meeting.status)}
                  </Tag>
                </div>
                {meeting.participants && (
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                    👥 {meeting.participants}
                  </Text>
                )}
                {meeting.agenda && (
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    📋 {meeting.agenda}
                  </Text>
                )}
                {meeting.conclusions && (
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    ✅ {meeting.conclusions}
                  </Text>
                )}
                {meeting.decisions && (
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    🎯 {meeting.decisions}
                  </Text>
                )}
              </div>
            </Card>
          </Col>
        ))}
        {meetingsData.length === 0 && (
          <Col span={24}>
            <Card>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <FileTextOutlined style={{ fontSize: '48px', color: '#D9D9D9', marginBottom: '16px' }} />
                <Title level={4} style={{ color: '#D9D9D9' }}>暂无家庭会议</Title>
                <Text type="secondary">点击"新增家庭会议"开始记录家庭会议</Text>
              </div>
            </Card>
          </Col>
        )}
      </Row>

      {/* 会议记录弹窗 */}
      <Modal
        title={`${selectedMeeting ? '编辑' : '新增'}家庭会议`}
        open={showMeetingModal}
        onCancel={() => setShowMeetingModal(false)}
        footer={null}
        width={600}
      >
        <Form
          form={meetingForm}
          layout="vertical"
          onFinish={handleSaveMeeting}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="meeting_type"
                label="会议类型"
                rules={[{ required: true, message: '请选择会议类型' }]}
              >
                <Select placeholder="请选择类型">
                  {familyConfig.meetingTypes.map(type => (
                    <Option key={type.key} value={type.key}>
                      <span style={{ marginRight: '8px' }}>{type.icon}</span>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="会议状态"
                rules={[{ required: true, message: '请选择会议状态' }]}
              >
                <Select placeholder="请选择状态">
                  <Option value="planned">已计划</Option>
                  <Option value="in_progress">进行中</Option>
                  <Option value="completed">已完成</Option>
                  <Option value="cancelled">已取消</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="title"
            label="会议标题"
            rules={[{ required: true, message: '请输入会议标题' }]}
          >
            <Input placeholder="请输入会议标题" />
          </Form.Item>
          
          <Form.Item
            name="meeting_date"
            label="会议日期"
          >
            <DatePicker 
              style={{ width: '100%' }}
              placeholder="选择会议日期"
              format="YYYY-MM-DD"
            />
          </Form.Item>
          
          <Form.Item
            name="participants"
            label="参会人员"
          >
            <Input placeholder="请输入参会人员名单" />
          </Form.Item>
          
          <Form.Item
            name="agenda"
            label="会议议题"
          >
            <TextArea rows={3} placeholder="请输入会议议题和讨论内容" />
          </Form.Item>
          
          <Form.Item
            name="conclusions"
            label="会议结论"
          >
            <TextArea rows={3} placeholder="请输入会议结论" />
          </Form.Item>
          
          <Form.Item
            name="decisions"
            label="会议决策"
          >
            <TextArea rows={3} placeholder="请输入会议决策和决议" />
          </Form.Item>
          
          <Form.Item
            name="action_items"
            label="行动项目"
          >
            <TextArea rows={3} placeholder="请输入需要执行的行动项目" />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea rows={2} placeholder="请输入备注信息" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedMeeting ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setShowMeetingModal(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// 子女成长档案组件
const FamilyChildren = () => {
  const [childrenData, setChildrenData] = useState([]);
  const [growthData, setGrowthData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showChildModal, setShowChildModal] = useState(false);
  const [showGrowthModal, setShowGrowthModal] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedGrowth, setSelectedGrowth] = useState(null);
  const [childForm] = Form.useForm();
  const [growthForm] = Form.useForm();

  // 获取子女数据
  const fetchChildrenData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || '';
      console.log('获取子女数据 - Token:', token ? '存在' : '不存在');
      console.log('Token长度:', token.length);
      
      if (!token) {
        message.error('请先登录');
        return;
      }
      
      const response = await axios.get(familyConfig.apiEndpoints.children);
      console.log('子女数据响应状态:', response.status);
      console.log('子女数据响应:', response.data);
      if (response.data?.success) {
        const children = response.data.children || [];
        console.log('设置子女数据:', children);
        setChildrenData(children);
      } else {
        message.error(response.data?.message || '获取子女数据失败');
      }
    } catch (error) {
      console.error('获取子女数据失败:', error);
      if (error.message && error.message.includes('Unexpected token')) {
        message.error('网络请求失败，请检查网络连接或重新登录');
      } else {
        message.error('获取子女数据失败: ' + (error.message || '未知错误'));
      }
    } finally {
      setLoading(false);
    }
  };

  // 获取成长记录数据
  const fetchGrowthData = async (children = null) => {
    try {
      const token = localStorage.getItem('token') || '';
      console.log('获取成长记录 - Token:', token ? '存在' : '不存在');
      
      if (!token) {
        message.error('请先登录');
        setGrowthData([]);
        return;
      }
      
      // 使用传入的children参数或当前状态中的childrenData
      const currentChildren = children || childrenData;
      console.log('当前子女数据:', currentChildren);
      
      if (!currentChildren || currentChildren.length === 0) {
        console.log('没有子女数据，设置成长记录为空数组');
        setGrowthData([]);
        return;
      }
      
      // 为每个子女获取成长记录
      const allGrowthRecords = [];
      for (const child of currentChildren) {
        try {
          console.log(`获取子女 ${child.name} (ID: ${child.id}) 的成长记录`);
          const response = await axios.get(`${familyConfig.apiEndpoints.growth}/${child.id}`);
          console.log(`子女 ${child.name} 成长记录响应状态:`, response.status);
          console.log(`子女 ${child.name} 成长记录响应:`, response.data);
          if (response.data?.success && response.data.growth) {
            allGrowthRecords.push(...response.data.growth);
          }
        } catch (error) {
          console.error(`获取子女 ${child.name} 的成长记录失败:`, error);
        }
      }
      
      // 按记录日期排序
      allGrowthRecords.sort((a, b) => new Date(b.record_date) - new Date(a.record_date));
      console.log('所有成长记录:', allGrowthRecords);
      setGrowthData(allGrowthRecords);
    } catch (error) {
      console.error('获取成长记录失败:', error);
      setGrowthData([]);
    }
  };

  // 打开子女信息弹窗
  const handleOpenChild = (child = null) => {
    setSelectedChild(child);
    if (child) {
      childForm.setFieldsValue({
        ...child,
        birth_date: child.birth_date ? dayjs(child.birth_date) : null
      });
    } else {
      childForm.resetFields();
    }
    setShowChildModal(true);
  };

  // 打开成长记录弹窗
  const handleOpenGrowth = (growth = null, childId = null) => {
    setSelectedGrowth(growth);
    if (growth) {
      growthForm.setFieldsValue({
        ...growth,
        record_date: growth.record_date ? dayjs(growth.record_date) : null
      });
    } else {
      growthForm.resetFields();
      if (childId) {
        growthForm.setFieldsValue({ child_id: childId });
      }
    }
    setShowGrowthModal(true);
  };

  // 保存子女信息
  const handleSaveChild = async (values) => {
    try {
      const token = localStorage.getItem('token') || '';
      const requestBody = {
        ...values,
        birth_date: values.birth_date ? values.birth_date.format('YYYY-MM-DD') : null
      };

      const url = selectedChild 
        ? `${familyConfig.apiEndpoints.updateChild}/${selectedChild.id}`
        : familyConfig.apiEndpoints.createChild;
      
      const method = selectedChild ? 'PUT' : 'POST';

      const response = selectedChild ? await axios.put(url, requestBody) : await axios.post(url, requestBody);
      console.log('保存子女响应:', response.data);
      if (response.data?.success) {
        message.success(selectedChild ? '更新成功' : '创建成功');
        setShowChildModal(false);
        childForm.resetFields();
        setSelectedChild(null);
        // 立即刷新数据
        await fetchChildrenData();
      } else {
        message.error(response.data?.message || '操作失败');
      }
    } catch (error) {
      console.error('保存子女信息失败:', error);
      message.error('操作失败');
    }
  };

  // 保存成长记录
  const handleSaveGrowth = async (values) => {
    try {
      const requestBody = {
        ...values,
        record_date: values.record_date ? values.record_date.format('YYYY-MM-DD') : null
      };

      const url = selectedGrowth 
        ? `${familyConfig.apiEndpoints.updateGrowth}/${selectedGrowth.id}`
        : familyConfig.apiEndpoints.createGrowth;
      
      const method = selectedGrowth ? 'PUT' : 'POST';
      const token = localStorage.getItem('token') || '';
      
      console.log('保存成长记录 - 操作类型:', selectedGrowth ? '更新' : '新增');
      console.log('请求URL:', url);
      console.log('请求方法:', method);
      console.log('请求体:', requestBody);
      console.log('Token:', token ? '存在' : '不存在');

      const response = selectedGrowth ? await axios.put(url, requestBody) : await axios.post(url, requestBody);
      console.log('成长记录保存响应状态:', response.status);
      console.log('成长记录保存响应:', response.data);
      if (response.data?.success) {
        message.success(selectedGrowth ? '更新成功' : '创建成功');
        setShowGrowthModal(false);
        fetchGrowthData();
      } else {
        message.error(response.data?.message || '操作失败');
      }
    } catch (error) {
      console.error('保存成长记录失败:', error);
      message.error('操作失败');
    }
  };

  // 删除子女信息
  const handleDeleteChild = async (id) => {
    try {
      const token = localStorage.getItem('token') || '';
      const response = await axios.delete(`${familyConfig.apiEndpoints.deleteChild}/${id}`);
      if (response.data?.success) {
        message.success('删除成功');
        fetchChildrenData();
      } else {
        message.error(response.data?.message || '删除失败');
      }
    } catch (error) {
      console.error('删除子女信息失败:', error);
      message.error('删除失败');
    }
  };

  // 删除成长记录
  const handleDeleteGrowth = async (id) => {
    try {
      const token = localStorage.getItem('token') || '';
      const url = `${familyConfig.apiEndpoints.deleteGrowth}/${id}`;
      
      console.log('删除成长记录 - ID:', id);
      console.log('请求URL:', url);
      console.log('Token:', token ? '存在' : '不存在');
      
      const response = await axios.delete(url);
      console.log('成长记录删除响应状态:', response.status);
      console.log('成长记录删除响应:', response.data);
      if (response.data?.success) {
        message.success('删除成功');
        fetchGrowthData();
      } else {
        message.error(response.data?.message || '删除失败');
      }
    } catch (error) {
      console.error('删除成长记录失败:', error);
      message.error('删除失败');
    }
  };

  // 获取类型颜色
  const getTypeColor = (typeKey) => {
    const type = familyConfig.growthTypes.find(t => t.key === typeKey);
    return type ? type.color : '#D9D9D9';
  };

  // 获取类型名称
  const getTypeName = (typeKey) => {
    const type = familyConfig.growthTypes.find(t => t.key === typeKey);
    return type ? type.name : '未知';
  };

  // 获取类型图标
  const getTypeIcon = (typeKey) => {
    const type = familyConfig.growthTypes.find(t => t.key === typeKey);
    return type ? type.icon : '📝';
  };

  // 计算年龄
  const calculateAge = (birthDate) => {
    if (!birthDate) return 0;
    const birth = dayjs(birthDate);
    const now = dayjs();
    return now.diff(birth, 'year');
  };

  // 获取子女姓名
  const getChildName = (childId) => {
    const child = childrenData.find(c => c.id === childId);
    return child ? child.name : '未知';
  };

  // 计算统计信息
  const totalChildren = childrenData.length;
  const totalGrowthRecords = growthData.length;
  const averageAge = totalChildren > 0 ? 
    Math.round(childrenData.reduce((sum, child) => sum + calculateAge(child.birth_date), 0) / totalChildren) : 0;

  // 组件加载时获取数据
  useEffect(() => {
    fetchChildrenData();
  }, []);

  // 当子女数据变化时，重新获取成长记录
  useEffect(() => {
    if (childrenData.length > 0) {
      console.log('子女数据变化，重新获取成长记录:', childrenData);
      fetchGrowthData(childrenData);
    } else {
      console.log('没有子女数据，清空成长记录');
      setGrowthData([]);
    }
  }, [childrenData]);

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="子女数量"
              value={totalChildren}
              prefix={<UserOutlined />}
              valueStyle={{ color: familyConfig.bgColor }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="成长记录"
              value={totalGrowthRecords}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#52C41A' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="平均年龄"
              value={averageAge}
              prefix={<CalendarOutlined />}
              suffix="岁"
              valueStyle={{ color: '#FAAD14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="记录频率"
              value={totalChildren > 0 ? Math.round(totalGrowthRecords / totalChildren) : 0}
              prefix={<BarChartOutlined />}
              suffix="条/人"
              valueStyle={{ color: '#1890FF' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 操作按钮 */}
      <div style={{ marginBottom: '16px' }}>
        <Space>
          <Button 
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenChild()}
            style={{ backgroundColor: familyConfig.bgColor, borderColor: familyConfig.bgColor }}
          >
            新增子女信息
          </Button>
          <Button 
            icon={<PlusOutlined />}
            onClick={() => handleOpenGrowth()}
            disabled={childrenData.length === 0}
          >
            新增成长记录
          </Button>
        </Space>
      </div>

      {/* 子女信息列表 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {childrenData.map((child) => (
          <Col xs={24} sm={12} md={8} lg={6} key={child.id}>
            <Card
              hoverable
              actions={[
                <EditOutlined key="edit" onClick={() => handleOpenChild(child)} />,
                <PlusOutlined key="add" onClick={() => handleOpenGrowth(null, child.id)} />,
                <Popconfirm
                  title="确定删除这个子女信息吗？"
                  onConfirm={() => handleDeleteChild(child.id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <DeleteOutlined key="delete" />
                </Popconfirm>
              ]}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                  {child.gender === 'male' ? '👦' : '👧'}
                </div>
                <Title level={5} style={{ margin: '8px 0' }}>
                  {child.name}
                </Title>
                <div style={{ marginBottom: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {child.gender === 'male' ? '男孩' : '女孩'} • {calculateAge(child.birth_date)}岁
                  </Text>
                </div>
                {child.birth_date && (
                  <div style={{ marginBottom: '8px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      📅 {dayjs(child.birth_date).format('YYYY-MM-DD')}
                    </Text>
                  </div>
                )}
                {child.notes && (
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                    {child.notes}
                  </Text>
                )}
              </div>
            </Card>
          </Col>
        ))}
        {childrenData.length === 0 && (
          <Col span={24}>
            <Card>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <UserOutlined style={{ fontSize: '48px', color: '#D9D9D9', marginBottom: '16px' }} />
                <Title level={4} style={{ color: '#D9D9D9' }}>暂无子女信息</Title>
                <Text type="secondary">点击"新增子女信息"开始记录孩子成长</Text>
              </div>
            </Card>
          </Col>
        )}
      </Row>

      {/* 成长记录列表 */}
      <Title level={4} style={{ marginBottom: '16px' }}>成长记录</Title>
      {growthData.length > 0 ? (
        <Row gutter={[16, 16]}>
          {growthData.map((growth) => (
            <Col xs={24} sm={12} md={8} lg={6} key={growth.id}>
              <Card
                hoverable
                actions={[
                  <EditOutlined key="edit" onClick={() => handleOpenGrowth(growth)} />,
                  <Popconfirm
                    title="确定删除这条成长记录吗？"
                    onConfirm={() => handleDeleteGrowth(growth.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <DeleteOutlined key="delete" />
                  </Popconfirm>
                ]}
              >
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                    {getTypeIcon(growth.record_type)}
                  </div>
                  <Title level={5} style={{ margin: '8px 0' }}>
                    {getTypeName(growth.record_type)}
                  </Title>
                  <Tag color={getTypeColor(growth.record_type)} style={{ marginBottom: '8px' }}>
                    {getChildName(growth.child_id)}
                  </Tag>
                  <div style={{ marginBottom: '8px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      📅 {growth.record_date ? dayjs(growth.record_date).format('MM-DD') : '未设置'}
                    </Text>
                  </div>
                  {growth.height && (
                    <div style={{ marginBottom: '4px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        📏 身高: {growth.height}cm
                      </Text>
                    </div>
                  )}
                  {growth.weight && (
                    <div style={{ marginBottom: '4px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        ⚖️ 体重: {growth.weight}kg
                      </Text>
                    </div>
                  )}
                  {growth.academic_status && (
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                      📚 {growth.academic_status}
                    </Text>
                  )}
                  {growth.hobbies && (
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                      🎨 {growth.hobbies}
                    </Text>
                  )}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <FileTextOutlined style={{ fontSize: '48px', color: '#D9D9D9', marginBottom: '16px' }} />
            <Title level={4} style={{ color: '#D9D9D9' }}>暂无成长记录</Title>
            <Text type="secondary">点击"新增成长记录"开始记录孩子的成长历程</Text>
          </div>
        </Card>
      )}

      {/* 子女信息弹窗 */}
      <Modal
        title={`${selectedChild ? '编辑' : '新增'}子女信息`}
        open={showChildModal}
        onCancel={() => setShowChildModal(false)}
        footer={null}
        width={600}
      >
        <Form
          form={childForm}
          layout="vertical"
          onFinish={handleSaveChild}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="姓名"
                rules={[{ required: true, message: '请输入姓名' }]}
              >
                <Input placeholder="请输入子女姓名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="gender"
                label="性别"
                rules={[{ required: true, message: '请选择性别' }]}
              >
                <Select placeholder="请选择性别">
                  <Option value="male">男孩</Option>
                  <Option value="female">女孩</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="birth_date"
            label="出生日期"
          >
            <DatePicker 
              style={{ width: '100%' }}
              placeholder="选择出生日期"
              format="YYYY-MM-DD"
            />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea rows={3} placeholder="请输入备注信息" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedChild ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setShowChildModal(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 成长记录弹窗 */}
      <Modal
        title={`${selectedGrowth ? '编辑' : '新增'}成长记录`}
        open={showGrowthModal}
        onCancel={() => setShowGrowthModal(false)}
        footer={null}
        width={600}
      >
        <Form
          form={growthForm}
          layout="vertical"
          onFinish={handleSaveGrowth}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="child_id"
                label="子女"
                rules={[{ required: true, message: '请选择子女' }]}
              >
                <Select placeholder="请选择子女">
                  {childrenData.map(child => (
                    <Option key={child.id} value={child.id}>
                      {child.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="record_type"
                label="记录类型"
                rules={[{ required: true, message: '请选择记录类型' }]}
              >
                <Select placeholder="请选择类型">
                  {familyConfig.growthTypes.map(type => (
                    <Option key={type.key} value={type.key}>
                      <span style={{ marginRight: '8px' }}>{type.icon}</span>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="record_date"
            label="记录日期"
          >
            <DatePicker 
              style={{ width: '100%' }}
              placeholder="选择记录日期"
              format="YYYY-MM-DD"
            />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="height"
                label="身高(cm)"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="请输入身高"
                  min={0}
                  max={300}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="weight"
                label="体重(kg)"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="请输入体重"
                  min={0}
                  max={200}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="academic_status"
            label="学业状况"
          >
            <Input placeholder="请输入学业状况" />
          </Form.Item>
          
          <Form.Item
            name="hobbies"
            label="兴趣爱好"
          >
            <Input placeholder="请输入兴趣爱好" />
          </Form.Item>
          
          <Form.Item
            name="skills"
            label="技能特长"
          >
            <Input placeholder="请输入技能特长" />
          </Form.Item>
          
          <Form.Item
            name="emotional_status"
            label="情绪状态"
          >
            <Input placeholder="请输入情绪状态" />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea rows={2} placeholder="请输入备注信息" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedGrowth ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setShowGrowthModal(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// 健康与生活方式管理组件
const FamilyHealth = () => {
  const [healthData, setHealthData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showHealthModal, setShowHealthModal] = useState(false);
  const [selectedHealth, setSelectedHealth] = useState(null);
  const [healthForm] = Form.useForm();

  // 获取健康数据
  const fetchHealthData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(familyConfig.apiEndpoints.health);
      if (response.data?.success) {
        setHealthData(response.data.health || []);
      } else {
        message.error(response.data?.message || '获取健康数据失败');
      }
    } catch (error) {
      console.error('获取健康数据失败:', error);
      message.error('获取健康数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 打开健康记录弹窗
  const handleOpenHealth = (health = null) => {
    setSelectedHealth(health);
    if (health) {
      healthForm.setFieldsValue({
        ...health,
        target_date: health.target_date ? dayjs(health.target_date) : null
      });
    } else {
      healthForm.resetFields();
    }
    setShowHealthModal(true);
  };

  // 保存健康记录
  const handleSaveHealth = async (values) => {
    try {
      const requestBody = {
        ...values,
        target_date: values.target_date ? values.target_date.format('YYYY-MM-DD') : null
      };

      const url = selectedHealth 
        ? `${familyConfig.apiEndpoints.updateHealth}/${selectedHealth.id}`
        : familyConfig.apiEndpoints.createHealth;
      
      const response = selectedHealth
        ? await axios.put(url, requestBody)
        : await axios.post(url, requestBody);

      if (response.data?.success) {
        message.success(selectedHealth ? '更新成功' : '创建成功');
        setShowHealthModal(false);
        fetchHealthData();
      } else {
        message.error(response.data?.message || '操作失败');
      }
    } catch (error) {
      console.error('保存健康记录失败:', error);
      message.error('操作失败');
    }
  };

  // 删除健康记录
  const handleDeleteHealth = async (id) => {
    try {
      const response = await axios.delete(`${familyConfig.apiEndpoints.deleteHealth}/${id}`);
      if (response.data?.success) {
        message.success('删除成功');
        fetchHealthData();
      } else {
        message.error(response.data?.message || '删除失败');
      }
    } catch (error) {
      console.error('删除健康记录失败:', error);
      message.error('删除失败');
    }
  };

  // 获取类型颜色
  const getTypeColor = (typeKey) => {
    const type = familyConfig.healthTypes.find(t => t.key === typeKey);
    return type ? type.color : '#D9D9D9';
  };

  // 获取类型名称
  const getTypeName = (typeKey) => {
    const type = familyConfig.healthTypes.find(t => t.key === typeKey);
    return type ? type.name : '未知';
  };

  // 获取类型图标
  const getTypeIcon = (typeKey) => {
    const type = familyConfig.healthTypes.find(t => t.key === typeKey);
    return type ? type.icon : '📝';
  };

  // 获取状态颜色
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'in_progress':
        return 'blue';
      case 'completed':
        return 'orange';
      case 'paused':
        return 'red';
      default:
        return 'default';
    }
  };

  // 获取状态名称
  const getStatusName = (status) => {
    switch (status) {
      case 'active':
        return '进行中';
      case 'in_progress':
        return '执行中';
      case 'completed':
        return '已完成';
      case 'paused':
        return '暂停';
      default:
        return '未知';
    }
  };

  // 计算统计信息
  const totalHealth = healthData.length;
  const activeHealth = healthData.filter(item => item.status === 'active').length;
  const completedHealth = healthData.filter(item => item.status === 'completed').length;
  const completionRate = totalHealth > 0 ? Math.round((completedHealth / totalHealth) * 100) : 0;

  // 组件加载时获取数据
  useEffect(() => {
    fetchHealthData();
  }, []);

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="健康计划"
              value={totalHealth}
              prefix={<HeartOutlined />}
              valueStyle={{ color: familyConfig.bgColor }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="进行中"
              value={activeHealth}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52C41A' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="已完成"
              value={completedHealth}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#FAAD14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="完成率"
              value={completionRate}
              prefix={<BarChartOutlined />}
              suffix="%"
              valueStyle={{ color: '#1890FF' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 操作按钮 */}
      <div style={{ marginBottom: '16px' }}>
        <Button 
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenHealth()}
          style={{ backgroundColor: familyConfig.bgColor, borderColor: familyConfig.bgColor }}
        >
          新增健康计划
        </Button>
      </div>

      {/* 健康记录列表 */}
      <Row gutter={[16, 16]}>
        {healthData.map((health) => (
          <Col xs={24} sm={12} md={8} lg={6} key={health.id}>
            <Card
              hoverable
              actions={[
                <EditOutlined key="edit" onClick={() => handleOpenHealth(health)} />,
                <Popconfirm
                  title="确定删除这个健康计划吗？"
                  onConfirm={() => handleDeleteHealth(health.id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <DeleteOutlined key="delete" />
                </Popconfirm>
              ]}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                  {getTypeIcon(health.health_type)}
                </div>
                <Title level={5} style={{ margin: '8px 0' }}>
                  {health.title}
                </Title>
                <Tag color={getTypeColor(health.health_type)} style={{ marginBottom: '8px' }}>
                  {getTypeName(health.health_type)}
                </Tag>
                {health.target_date && (
                  <div style={{ marginBottom: '8px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      📅 {dayjs(health.target_date).format('MM-DD')}
                    </Text>
                  </div>
                )}
                <div style={{ marginTop: '8px' }}>
                  <Tag color={getStatusColor(health.status)}>
                    {getStatusName(health.status)}
                  </Tag>
                </div>
                {health.description && (
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                    {health.description}
                  </Text>
                )}
              </div>
            </Card>
          </Col>
        ))}
        {healthData.length === 0 && (
          <Col span={24}>
            <Card>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <HeartOutlined style={{ fontSize: '48px', color: '#D9D9D9', marginBottom: '16px' }} />
                <Title level={4} style={{ color: '#D9D9D9' }}>暂无健康计划</Title>
                <Text type="secondary">点击"新增健康计划"开始管理家庭健康</Text>
              </div>
            </Card>
          </Col>
        )}
      </Row>

      {/* 健康记录弹窗 */}
      <Modal
        title={`${selectedHealth ? '编辑' : '新增'}健康计划`}
        open={showHealthModal}
        onCancel={() => setShowHealthModal(false)}
        footer={null}
        width={600}
      >
        <Form
          form={healthForm}
          layout="vertical"
          onFinish={handleSaveHealth}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="health_type"
                label="健康类型"
                rules={[{ required: true, message: '请选择健康类型' }]}
              >
                <Select placeholder="请选择类型">
                  {familyConfig.healthTypes.map(type => (
                    <Option key={type.key} value={type.key}>
                      <span style={{ marginRight: '8px' }}>{type.icon}</span>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="计划状态"
                rules={[{ required: true, message: '请选择计划状态' }]}
              >
                <Select placeholder="请选择状态">
                  <Option value="active">进行中</Option>
                  <Option value="in_progress">执行中</Option>
                  <Option value="completed">已完成</Option>
                  <Option value="paused">暂停</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="title"
            label="计划标题"
            rules={[{ required: true, message: '请输入计划标题' }]}
          >
            <Input placeholder="请输入健康计划标题" />
          </Form.Item>
          
          <Form.Item
            name="target_date"
            label="目标日期"
          >
            <DatePicker 
              style={{ width: '100%' }}
              placeholder="选择目标日期"
              format="YYYY-MM-DD"
            />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="计划描述"
          >
            <TextArea rows={3} placeholder="请输入健康计划详细描述" />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea rows={2} placeholder="请输入备注信息" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedHealth ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setShowHealthModal(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// 沟通记录组件
const FamilyCommunication = () => {
  const [communicationData, setCommunicationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCommunicationModal, setShowCommunicationModal] = useState(false);
  const [selectedCommunication, setSelectedCommunication] = useState(null);
  const [communicationForm] = Form.useForm();

  // 监听selectedCommunication变化，设置表单初始值
  useEffect(() => {
    if (showCommunicationModal && selectedCommunication) {
      setTimeout(() => {
        communicationForm.setFieldsValue({
          communication_type: selectedCommunication.communication_type,
          title: selectedCommunication.title,
          participants: selectedCommunication.participants,
          communication_date: selectedCommunication.communication_date ? dayjs(selectedCommunication.communication_date) : null,
          content: selectedCommunication.content,
          outcome: selectedCommunication.outcome,
          follow_up: selectedCommunication.follow_up,
          notes: selectedCommunication.notes
        });
      }, 100);
    } else if (showCommunicationModal && !selectedCommunication) {
      communicationForm.resetFields();
    }
  }, [showCommunicationModal, selectedCommunication, communicationForm]);

  // 获取当前用户ID
  const getCurrentUserId = useMemo(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        if (parsed?.id) {
          return parsed.id;
        }
      }
      const storedId = localStorage.getItem('userId');
      if (storedId) {
        return storedId;
      }
    } catch (e) {
      console.warn('解析用户信息失败:', e);
    }
    return null;
  }, []);

  // 获取沟通数据
  const fetchCommunicationData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(familyConfig.apiEndpoints.communication, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data?.success) {
        setCommunicationData(response.data.communication || []);
      } else {
        message.error(response.data?.message || '获取沟通数据失败');
      }
    } catch (error) {
      console.error('获取沟通数据失败:', error);
      message.error('获取沟通数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 打开沟通记录弹窗
  const handleOpenCommunication = (communication = null) => {
    console.log('打开沟通记录弹窗:', communication);
    setSelectedCommunication(communication);
    setShowCommunicationModal(true);
    
    // 使用setTimeout确保表单已经渲染
    setTimeout(() => {
      if (communication) {
        communicationForm.setFieldsValue({
          communication_type: communication.communication_type,
          title: communication.title,
          participants: communication.participants,
          communication_date: communication.communication_date ? dayjs(communication.communication_date) : null,
          content: communication.content,
          outcome: communication.outcome,
          follow_up: communication.follow_up,
          notes: communication.notes
        });
      } else {
        communicationForm.resetFields();
      }
    }, 100);
  };

  // 保存沟通记录
  const handleSaveCommunication = async (values) => {
    try {
      const userId = getCurrentUserId;
      if (!userId) {
        message.error('用户未登录，请重新登录');
        return;
      }

      const requestBody = {
        communication_type: values.communication_type,
        title: values.title,
        participants: values.participants,
        communication_date: values.communication_date ? values.communication_date.format('YYYY-MM-DD') : null,
        content: values.content || '',
        outcome: values.outcome || '',
        follow_up: values.follow_up || '',
        notes: values.notes || ''
      };

      const url = selectedCommunication 
        ? `${familyConfig.apiEndpoints.updateCommunication}/${selectedCommunication.id}`
        : familyConfig.apiEndpoints.createCommunication;
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      const response = selectedCommunication
        ? await axios.put(url, requestBody, config)
        : await axios.post(url, requestBody, config);

      if (response.data?.success) {
        message.success(selectedCommunication ? '更新成功' : '创建成功');
        setShowCommunicationModal(false);
        fetchCommunicationData();
      } else {
        message.error(response.data?.message || '操作失败');
      }
    } catch (error) {
      console.error('保存沟通记录失败:', error);
      message.error('操作失败');
    }
  };

  // 删除沟通记录
  const handleDeleteCommunication = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${familyConfig.apiEndpoints.deleteCommunication}/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data?.success) {
        message.success('删除成功');
        fetchCommunicationData();
      } else {
        message.error(response.data?.message || '删除失败');
      }
    } catch (error) {
      console.error('删除沟通记录失败:', error);
      message.error('删除失败');
    }
  };

  // 获取类型颜色
  const getTypeColor = (typeKey) => {
    const type = familyConfig.communicationTypes.find(t => t.key === typeKey);
    return type ? type.color : '#D9D9D9';
  };

  // 获取类型名称
  const getTypeName = (typeKey) => {
    const type = familyConfig.communicationTypes.find(t => t.key === typeKey);
    return type ? type.name : '未知';
  };

  // 获取类型图标
  const getTypeIcon = (typeKey) => {
    const type = familyConfig.communicationTypes.find(t => t.key === typeKey);
    return type ? type.icon : '📝';
  };

  // 计算统计信息
  const totalCommunications = communicationData.length;
  const recentCommunications = communicationData.filter(item => {
    const commDate = dayjs(item.communication_date);
    const now = dayjs();
    return commDate.isAfter(now.subtract(7, 'day'));
  }).length;

  // 组件加载时获取数据
  useEffect(() => {
    fetchCommunicationData();
  }, []);

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="沟通记录"
              value={totalCommunications}
              prefix={<MessageOutlined />}
              valueStyle={{ color: familyConfig.bgColor }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="本周沟通"
              value={recentCommunications}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#FAAD14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="本周沟通"
              value={recentCommunications}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#FAAD14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="沟通频率"
              value={totalCommunications > 0 ? Math.round(totalCommunications / 7) : 0}
              prefix={<BarChartOutlined />}
              suffix="次/周"
              valueStyle={{ color: '#1890FF' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 操作按钮 */}
      <div style={{ marginBottom: '16px' }}>
        <Button 
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenCommunication()}
          style={{ backgroundColor: familyConfig.bgColor, borderColor: familyConfig.bgColor }}
        >
          新增沟通记录
        </Button>
      </div>

      {/* 沟通记录列表 */}
      <Row gutter={[16, 16]}>
        {communicationData.map((communication) => (
          <Col xs={24} sm={12} md={8} lg={6} key={communication.id}>
            <Card
              hoverable
              actions={[
                <EditOutlined key="edit" onClick={() => handleOpenCommunication(communication)} />,
                <Popconfirm
                  title="确定删除这条沟通记录吗？"
                  onConfirm={() => handleDeleteCommunication(communication.id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <DeleteOutlined key="delete" />
                </Popconfirm>
              ]}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                  {getTypeIcon(communication.communication_type)}
                </div>
                <Title level={5} style={{ margin: '8px 0' }}>
                  {communication.title}
                </Title>
                <Tag color={getTypeColor(communication.communication_type)} style={{ marginBottom: '8px' }}>
                  {getTypeName(communication.communication_type)}
                </Tag>
                <div style={{ marginBottom: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    👥 {communication.participants}
                  </Text>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    📅 {communication.communication_date ? dayjs(communication.communication_date).format('MM-DD') : '未设置'}
                  </Text>
                </div>

                {communication.content && (
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                    💬 {communication.content}
                  </Text>
                )}
                {communication.outcome && (
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    ✅ {communication.outcome}
                  </Text>
                )}
                {communication.follow_up && (
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    📋 {communication.follow_up}
                  </Text>
                )}
              </div>
            </Card>
          </Col>
        ))}
        {communicationData.length === 0 && (
          <Col span={24}>
            <Card>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <MessageOutlined style={{ fontSize: '48px', color: '#D9D9D9', marginBottom: '16px' }} />
                <Title level={4} style={{ color: '#D9D9D9' }}>暂无沟通记录</Title>
                <Text type="secondary">点击"新增沟通记录"开始记录家庭沟通</Text>
              </div>
            </Card>
          </Col>
        )}
      </Row>

      {/* 沟通记录弹窗 */}
      <Drawer
        title={`${selectedCommunication ? '编辑' : '新增'}沟通记录`}
        open={showCommunicationModal}
        onClose={() => setShowCommunicationModal(false)}
        width={600}
        placement="right"
      >
        {showCommunicationModal && (
          <Form
            form={communicationForm}
            layout="vertical"
            onFinish={handleSaveCommunication}
            preserve={false}
          >
          <Form.Item
            name="communication_type"
            label="沟通类型"
            rules={[{ required: true, message: '请选择沟通类型' }]}
          >
            <Select placeholder="请选择类型">
              {familyConfig.communicationTypes.map(type => (
                <Option key={type.key} value={type.key}>
                  <span style={{ marginRight: '8px' }}>{type.icon}</span>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="title"
            label="沟通主题"
            rules={[{ required: true, message: '请输入沟通主题' }]}
          >
            <Input placeholder="请输入沟通主题" />
          </Form.Item>
          
          <Form.Item
            name="participants"
            label="参与人员"
            rules={[{ required: true, message: '请输入参与人员' }]}
          >
            <Input placeholder="请输入参与沟通的家庭成员" />
          </Form.Item>
          
          <Form.Item
            name="communication_date"
            label="沟通日期"
          >
            <DatePicker 
              style={{ width: '100%' }}
              placeholder="选择沟通日期"
              format="YYYY-MM-DD"
            />
          </Form.Item>
          
          <Form.Item
            name="content"
            label="沟通内容"
          >
            <TextArea rows={3} placeholder="请输入沟通详细内容" />
          </Form.Item>
          
          <Form.Item
            name="outcome"
            label="沟通结果"
          >
            <TextArea rows={3} placeholder="请输入沟通结果和达成的共识" />
          </Form.Item>
          
          <Form.Item
            name="follow_up"
            label="后续跟进"
          >
            <TextArea rows={3} placeholder="请输入后续需要跟进的事项" />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea rows={2} placeholder="请输入备注信息" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedCommunication ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setShowCommunicationModal(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
          </Form>
        )}
      </Drawer>
    </div>
  );
};

const Family = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [showTipsModal, setShowTipsModal] = useState(false);
  const [familyStats, setFamilyStats] = useState(INITIAL_FAMILY_STATS);
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchFamilyStats = useCallback(async () => {
    if (typeof window === 'undefined') {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setFamilyStats(INITIAL_FAMILY_STATS);
      setStatsLoading(false);
      return;
    }

    setStatsLoading(true);

    try {
      const requests = [
        axios.get(familyConfig.apiEndpoints.chores),
        axios.get(familyConfig.apiEndpoints.finance),
        axios.get(familyConfig.apiEndpoints.schedule),
        axios.get(familyConfig.apiEndpoints.meetings),
        axios.get(familyConfig.apiEndpoints.children),
        axios.get(familyConfig.apiEndpoints.health),
        axios.get(familyConfig.apiEndpoints.communication)
      ];

      const [
        choresResult,
        financeResult,
        scheduleResult,
        meetingsResult,
        childrenResult,
        healthResult,
        communicationResult
      ] = await Promise.allSettled(requests);

      const extractList = (result, key) => {
        if (result.status !== 'fulfilled') {
          console.error(`获取${key}数据失败:`, result.reason);
          return [];
        }
        const payload = result.value?.data;
        const list = payload?.[key];
        return Array.isArray(list) ? list : [];
      };

      const chores = extractList(choresResult, 'chores');
      const finance = extractList(financeResult, 'finance');
      const schedule = extractList(scheduleResult, 'schedule');
      const meetings = extractList(meetingsResult, 'meetings');
      const children = extractList(childrenResult, 'children');
      const health = extractList(healthResult, 'health');
      const communication = extractList(communicationResult, 'communication');

      const memberSet = new Set();
      chores.forEach(item => {
        if (item?.member_name) {
          memberSet.add(String(item.member_name).trim());
        } else if (item?.member) {
          memberSet.add(String(item.member).trim());
        }
      });

      const budgetCandidates = finance.filter(item => {
        const type = item?.type;
        const category = item?.category;
        if (typeof type === 'string') {
          return ['budget', 'monthly_budget', 'budget_plan'].includes(type);
        }
        if (typeof category === 'string') {
          return category.includes('预算');
        }
        return false;
      });

      const financeSource = budgetCandidates.length > 0 ? budgetCandidates : finance;
      const totalBudget = financeSource.reduce((sum, item) => {
        const amount = parseFloat(item?.amount);
        return sum + (Number.isFinite(amount) ? amount : 0);
      }, 0);

      const completedChores = chores.filter(item => item?.status === 'completed').length;

      const now = dayjs();
      const upcomingEvents = schedule.filter(item => {
        const status = item?.status;
        if (status === 'planned' || status === 'in_progress') {
          return true;
        }
        if (status === 'completed' || status === 'cancelled') {
          return false;
        }
        if (item?.start_date) {
          const start = dayjs(item.start_date);
          return start.isAfter(now, 'day') || start.isSame(now, 'day');
        }
        return false;
      }).length;

      setFamilyStats({
        totalMembers: memberSet.size,
        totalBudget: Math.round(totalBudget * 100) / 100,
        completedChores,
        upcomingEvents,
        recentMeetings: meetings.length,
        childrenCount: children.length,
        healthRecords: health.length,
        communicationRecords: communication.length
      });
    } catch (error) {
      console.error('获取家庭概览统计失败:', error);
      setFamilyStats(INITIAL_FAMILY_STATS);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchFamilyStats();
    }
  }, [activeTab, fetchFamilyStats]);

  // 家庭管理TIPS弹窗
  const showFamilyTips = () => {
    setShowTipsModal(true);
  };

  // 获取类型颜色
  const getTypeColor = (typeKey, typeList) => {
    const type = typeList.find(t => t.key === typeKey);
    return type ? type.color : '#D9D9D9';
  };

  // 获取类型名称
  const getTypeName = (typeKey, typeList) => {
    const type = typeList.find(t => t.key === typeKey);
    return type ? type.name : '未知';
  };

  // 获取类型图标
  const getTypeIcon = (typeKey, typeList) => {
    const type = typeList.find(t => t.key === typeKey);
    return type ? type.icon : '📝';
  };

  return (
    <div className="mod-page">
      {/* 页头 */}
      <div className="mod-header">
        <div>
          <div className="mod-header-title">
            <HomeOutlined style={{ marginRight: 10 }} />
            {familyConfig.name}
          </div>
          <div className="mod-header-subtitle">
            财务 · 家务 · 时间 · 会议 · 成长 · 健康 · 沟通 — 一站式记录与规划家庭日常
          </div>
        </div>
        <Space wrap>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
          >
            返回
          </Button>
          <Button
            icon={<BulbOutlined />}
            onClick={showFamilyTips}
            style={{ borderColor: familyConfig.bgColor, color: familyConfig.bgColor }}
          >
            家庭TIPS
          </Button>
        </Space>
      </div>

      {/* 主要内容 */}
      <div>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'overview',
              label: (
                <span>
                  <HomeOutlined />
                  家庭概览
                </span>
              ),
              children: (
                <div>
                  {/* 统计卡片 */}
                  <div className="mod-stats" style={{ opacity: statsLoading ? 0.6 : 1, transition: 'opacity .2s' }}>
                    <div className="mod-stat-card clickable" onClick={() => setActiveTab('chores')}>
                      <div className="mod-stat-icon" style={{ background: 'linear-gradient(135deg,#6366f1,#818cf8)' }}><UserOutlined /></div>
                      <div>
                        <div className="mod-stat-value">{familyStats.totalMembers}</div>
                        <div className="mod-stat-label">家庭成员</div>
                      </div>
                    </div>
                    <div className="mod-stat-card clickable" onClick={() => setActiveTab('finance')}>
                      <div className="mod-stat-icon" style={{ background: 'linear-gradient(135deg,#22c55e,#4ade80)' }}><DollarOutlined /></div>
                      <div>
                        <div className="mod-stat-value">{familyStats.totalBudget}</div>
                        <div className="mod-stat-label">月度预算(元)</div>
                      </div>
                    </div>
                    <div className="mod-stat-card clickable" onClick={() => setActiveTab('chores')}>
                      <div className="mod-stat-icon" style={{ background: 'linear-gradient(135deg,#0ea5e9,#38bdf8)' }}><CheckCircleOutlined /></div>
                      <div>
                        <div className="mod-stat-value">{familyStats.completedChores}</div>
                        <div className="mod-stat-label">已完成家务</div>
                      </div>
                    </div>
                    <div className="mod-stat-card clickable" onClick={() => setActiveTab('schedule')}>
                      <div className="mod-stat-icon" style={{ background: 'linear-gradient(135deg,#f59e0b,#fbbf24)' }}><CalendarOutlined /></div>
                      <div>
                        <div className="mod-stat-value">{familyStats.upcomingEvents}</div>
                        <div className="mod-stat-label">即将活动</div>
                      </div>
                    </div>
                  </div>

                  {/* 功能导航 */}
                  <div className="mod-section-title" style={{ marginBottom: 12 }}>
                    <AppstoreOutlined style={{ marginRight: 6, color: familyConfig.bgColor }} />功能模块
                  </div>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8}>
                      <Card
                        hoverable
                        onClick={() => setActiveTab('finance')}
                        style={{ textAlign: 'center', cursor: 'pointer' }}
                      >
                        <DollarOutlined style={{ fontSize: '48px', color: '#52C41A', marginBottom: '16px' }} />
                        <Title level={4}>家庭财务表</Title>
                        <Text type="secondary">预算、存款、投资、保险管理</Text>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Card
                        hoverable
                        onClick={() => setActiveTab('chores')}
                        style={{ textAlign: 'center', cursor: 'pointer' }}
                      >
                        <TeamOutlined style={{ fontSize: '48px', color: '#1890FF', marginBottom: '16px' }} />
                        <Title level={4}>家务分工表</Title>
                        <Text type="secondary">家庭成员分工协作管理</Text>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Card
                        hoverable
                        onClick={() => setActiveTab('schedule')}
                        style={{ textAlign: 'center', cursor: 'pointer' }}
                      >
                        <CalendarOutlined style={{ fontSize: '48px', color: '#FAAD14', marginBottom: '16px' }} />
                        <Title level={4}>家庭时间表</Title>
                        <Text type="secondary">活动安排和旅行记录</Text>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Card
                        hoverable
                        onClick={() => setActiveTab('meetings')}
                        style={{ textAlign: 'center', cursor: 'pointer' }}
                      >
                        <FileTextOutlined style={{ fontSize: '48px', color: '#722ED1', marginBottom: '16px' }} />
                        <Title level={4}>家庭会议记录</Title>
                        <Text type="secondary">会议管理和决策记录</Text>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Card
                        hoverable
                        onClick={() => setActiveTab('children')}
                        style={{ textAlign: 'center', cursor: 'pointer' }}
                      >
                        <UserOutlined style={{ fontSize: '48px', color: '#13C2C2', marginBottom: '16px' }} />
                        <Title level={4}>子女成长档案</Title>
                        <Text type="secondary">孩子成长数据记录</Text>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Card
                        hoverable
                        onClick={() => setActiveTab('health')}
                        style={{ textAlign: 'center', cursor: 'pointer' }}
                      >
                        <HeartOutlined style={{ fontSize: '48px', color: '#FF6B6B', marginBottom: '16px' }} />
                        <Title level={4}>健康与生活方式</Title>
                        <Text type="secondary">家庭健康习惯管理</Text>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Card
                        hoverable
                        onClick={() => setActiveTab('communication')}
                        style={{ textAlign: 'center', cursor: 'pointer' }}
                      >
                        <MessageOutlined style={{ fontSize: '48px', color: '#A9DFBF', marginBottom: '16px' }} />
                        <Title level={4}>沟通记录</Title>
                        <Text type="secondary">家庭成员沟通历史</Text>
                      </Card>
                    </Col>
                  </Row>
                </div>
              )
            },
            {
              key: 'finance',
              label: (
                <span>
                  <DollarOutlined />
                  家庭财务
                </span>
              ),
              children: <FamilyFinance />
            },
            {
              key: 'chores',
              label: (
                <span>
                  <TeamOutlined />
                  家务分工
                </span>
              ),
              children: <FamilyChores />
            },
            {
              key: 'schedule',
              label: (
                <span>
                  <CalendarOutlined />
                  家庭时间
                </span>
              ),
              children: <FamilySchedule />
            },
            {
              key: 'meetings',
              label: (
                <span>
                  <FileTextOutlined />
                  家庭会议
                </span>
              ),
              children: <FamilyMeetings />
            },
            {
              key: 'children',
              label: (
                <span>
                  <UserOutlined />
                  子女成长
                </span>
              ),
              children: <FamilyChildren />
            },
            {
              key: 'health',
              label: (
                <span>
                  <HeartOutlined />
                  健康管理
                </span>
              ),
              children: <FamilyHealth />
            },
            {
              key: 'communication',
              label: (
                <span>
                  <MessageOutlined />
                  沟通记录
                </span>
              ),
              children: <FamilyCommunication />
            }
          ]}
        />
      </div>

      {/* 家庭TIPS弹窗 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <BulbOutlined style={{ color: familyConfig.bgColor, marginRight: '8px' }} />
            家庭管理TIPS
          </div>
        }
        open={showTipsModal}
        onCancel={() => setShowTipsModal(false)}
        footer={null}
        width={800}
        style={{ maxHeight: '80vh', overflow: 'auto' }}
      >
        <div style={{ padding: '16px 0' }}>
          {/* 家庭管理原则 */}
          <Card title="家庭管理五大原则" style={{ marginBottom: '16px' }}>
            <Steps direction="vertical" size="small">
              {familyConfig.familyPrinciples.principles.map((principle, index) => (
                <Step
                  key={index}
                  title={principle.title}
                  description={principle.description}
                  icon={<span style={{ fontSize: '16px' }}>{principle.icon}</span>}
                />
              ))}
            </Steps>
          </Card>

          {/* 家庭管理TIPS */}
          <Card title="家庭管理TIPS" style={{ marginBottom: '16px' }}>
            <Timeline>
              {familyConfig.familyManagementTips.tips.map((tip, index) => (
                <Timeline.Item key={index}>
                  <Title level={5}>
                    <span style={{ marginRight: '8px' }}>{tip.icon}</span>
                    {tip.title}
                  </Title>
                  <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
                    {tip.description}
                  </Text>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>

          {/* 功能说明 */}
          <Card title="功能模块说明">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>💰 家庭财务表</Title>
                  <Text type="secondary">管理月度预算、存款目标、股票基金、保险、固定资产等</Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>👥 家务分工表</Title>
                  <Text type="secondary">记录家庭成员身份、姓名、家务内容、完成情况</Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>📅 家庭时间表</Title>
                  <Text type="secondary">安排周末家庭活动、记录定期旅行或准备</Text>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>📋 家庭会议记录</Title>
                  <Text type="secondary">记录会议时间、议题、结论和决策</Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>👶 子女成长档案</Title>
                  <Text type="secondary">记录时间、年龄、身高、体重、学业状况、爱好兴趣</Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>💬 沟通记录</Title>
                  <Text type="secondary">记录与家庭成员的沟通历史和重要谈话</Text>
                </div>
              </Col>
            </Row>
          </Card>
        </div>
      </Modal>
    </div>
  );
};

export default Family;
